import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  EpisodeListItem,
  LoadingSkeleton,
  PriceBadge,
  Sheet,
  SheetContent,
  SheetTitle,
  VerticalPosterCard,
  cn,
} from "@bloom/ui";
import { Lock, Play, Plus, Check, Heart } from "lucide-react";
import { categoryLabel } from "@bloom/types";
import { makeT } from "@bloom/i18n";
import { useLocale } from "../locale/locale-context.tsx";
import { useAuth } from "../auth/auth-context.tsx";
import { api, ApiError } from "../api/client.ts";
import { STREAM_EMBED_ORIGIN } from "../config/stream.ts";

interface SeriesCast {
  name: string;
  role?: string;
}

interface SeriesRatings {
  dislike: number;
  like: number;
  super: number;
  total: number;
  score: number;
}

interface Series {
  _id: string;
  slug: string;
  titleTh: string;
  titleEn: string;
  taglineTh: string | null;
  taglineEn: string | null;
  synopsisTh: string;
  synopsisEn: string;
  posterUrl: string;
  categories: string[];
  episodeCount: number;
  seasonsCount: number;
  year: number | null;
  ageRating: string | null;
  cast: SeriesCast[];
  ratings: SeriesRatings | null;
  freeEpisodeCount: number | null;
  bundlePriceThb: number | null;
  trailerStreamVideoId: string | null;
}

interface Episode {
  _id: string;
  seasonNumber: number;
  episodeNumber: number;
  titleTh: string;
  titleEn: string;
  descriptionTh: string | null;
  descriptionEn: string | null;
  durationSec: number;
  thumbnailUrl: string;
  isFree: boolean;
  priceThb: number | null;
}

interface SiblingSeries {
  _id: string;
  slug: string;
  titleTh: string;
  titleEn: string;
  taglineTh: string | null;
  taglineEn: string | null;
  posterUrl: string;
  freeEpisodeCount: number | null;
}

export function SeriesDetailPage() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const { locale, t } = useLocale();
  const { user } = useAuth();

  const [series, setSeries] = useState<Series | null>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [siblings, setSiblings] = useState<SiblingSeries[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [trailerOpen, setTrailerOpen] = useState(false);
  const [unlockEp, setUnlockEp] = useState<Episode | null>(null);
  const [inList, setInList] = useState(false);
  const [listBusy, setListBusy] = useState(false);
  // Wallet balance for the unlock sheet — null while loading, undefined when
  // signed out, number when known. Wallet is the primary unlock path per the
  // round-5 payment decision (see docs/payment-providers-thailand.md).
  const [walletBalance, setWalletBalance] = useState<number | null | undefined>(undefined);
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    void (async () => {
      try {
        const detail = await api.get<{ series: Series }>(`/api/series/${slug}`);
        setSeries(detail.series);
        const eps = await api.get<{ items: Episode[] }>(`/api/series/${slug}/episodes`);
        setEpisodes(eps.items);
        const firstCat = detail.series.categories[0];
        if (firstCat) {
          const more = await api.get<{ items: SiblingSeries[] }>(
            `/api/series?category=${encodeURIComponent(firstCat)}&limit=8`,
          );
          setSiblings(more.items.filter((s) => s.slug !== detail.series.slug).slice(0, 6));
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Watchlist membership — only fetched when authenticated. Cheap GET, fine
  // to do on mount; the response is small.
  useEffect(() => {
    if (!user || !series) return;
    void (async () => {
      try {
        const data = await api.get<{ items: Array<{ seriesId: string }> }>("/api/me/my-list");
        setInList(data.items.some((e) => e.seriesId === series._id));
      } catch {
        setInList(false);
      }
    })();
  }, [user, series]);

  // Wallet balance — drives the unlock-sheet primary CTA. Refresh whenever
  // the unlock sheet opens so we have a current number.
  useEffect(() => {
    if (!user) {
      setWalletBalance(undefined);
      return;
    }
    void (async () => {
      try {
        const data = await api.get<{ wallet: { balanceThb: number } }>("/api/wallet");
        setWalletBalance(data.wallet.balanceThb);
      } catch {
        setWalletBalance(0);
      }
    })();
  }, [user, unlockEp]);

  // Wallet-driven unlock: debit, then navigate to the player.
  const unlockWithWallet = async (kind: "episode" | "bundle") => {
    if (!series || !unlockEp) return;
    if (!user) {
      navigate(`/login?next=/series/${series.slug}`);
      return;
    }
    setUnlockBusy(true);
    setUnlockError(null);
    try {
      if (kind === "episode") {
        await api.post(`/api/wallet/unlock/episode`, {
          seriesSlug: series.slug,
          seasonNumber: unlockEp.seasonNumber ?? 1,
          episodeNumber: unlockEp.episodeNumber,
        });
        setUnlockEp(null);
        navigate(`/watch/${series.slug}/${unlockEp.seasonNumber ?? 1}/${unlockEp.episodeNumber}`);
      } else {
        await api.post(`/api/wallet/unlock/bundle`, { seriesSlug: series.slug });
        setUnlockEp(null);
        navigate(`/watch/${series.slug}/${unlockEp.seasonNumber ?? 1}/1`);
      }
    } catch (err) {
      // 402 short-balance → message includes the gap; route the user to
      // /account/wallet?need=N&next=… so they can top up and come back.
      const msg = (err as Error).message;
      const shortMatch = /short.*?(\d+)/i.exec(msg);
      if (shortMatch) {
        const need = Number(shortMatch[1]);
        navigate(`/account/wallet?need=${need}&next=/series/${series.slug}`);
        return;
      }
      setUnlockError(msg);
    } finally {
      setUnlockBusy(false);
    }
  };

  const toggleList = async () => {
    if (!series) return;
    if (!user) {
      navigate(`/login?next=/series/${series.slug}`);
      return;
    }
    setListBusy(true);
    try {
      if (inList) {
        await api.delete(`/api/me/my-list/${series._id}`);
        setInList(false);
      } else {
        await api.post(`/api/me/my-list/${series._id}`);
        setInList(true);
      }
    } finally {
      setListBusy(false);
    }
  };

  const title = useMemo(
    () => (series ? (locale === "th" ? series.titleTh : series.titleEn) : ""),
    [series, locale],
  );
  const tagline = useMemo(() => {
    if (!series) return "";
    const v = locale === "th" ? series.taglineTh : series.taglineEn;
    return v?.trim() ?? "";
  }, [series, locale]);
  const synopsis = useMemo(
    () => (series ? (locale === "th" ? series.synopsisTh : series.synopsisEn) : ""),
    [series, locale],
  );

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6">
        <LoadingSkeleton className="aspect-[3/4] w-full sm:aspect-[16/7]" />
        <LoadingSkeleton className="h-10 w-2/3" />
        <LoadingSkeleton className="h-24" />
      </div>
    );
  }
  if (notFound || !series) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="font-display text-4xl">{t("errors.not_found")}</h1>
        <Link
          to="/series"
          className="mt-6 inline-flex h-11 items-center justify-center rounded-full bg-[var(--brand-ink)] px-6 text-sm font-semibold text-[var(--brand-paper)]"
        >
          {t("nav.all_series")}
        </Link>
      </div>
    );
  }

  const freeCount = series.freeEpisodeCount ?? 3;
  const bundlePrice = series.bundlePriceThb ?? 99;
  const firstEpisode = episodes[0];
  const ratingBadge = deriveRatingBadge(series.ratings, locale);

  return (
    <div className="space-y-12 pb-16">
      {/* Hero — blurred poster wash with the actual portrait poster on the
          left. The wash mirrors the landing's pink-rich palette but pulls its
          color from the actual poster so each series feels distinct. */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${series.posterUrl})` }}
        />
        <div aria-hidden className="absolute inset-0 backdrop-blur-2xl backdrop-saturate-150" />
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-[var(--brand-paper)]"
        />

        <div className="relative mx-auto grid max-w-7xl gap-8 px-4 pb-10 pt-10 sm:px-6 sm:pt-14 md:grid-cols-[260px_1fr] md:gap-10 lg:px-8">
          <div className="mx-auto w-full max-w-[260px] md:mx-0">
            <div className="aspect-poster overflow-hidden rounded-2xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
              <img
                src={series.posterUrl}
                alt={title}
                className="h-full w-full object-cover"
                fetchPriority="high"
              />
            </div>
          </div>

          <div className="space-y-5 text-white">
            <div className="space-y-2">
              <h1 className="font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">
                {title}
              </h1>
              {tagline ? (
                <p className="max-w-prose text-base text-white/85 sm:text-lg">{tagline}</p>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
              {series.year ? (
                <span className="rounded-full bg-white/15 px-2.5 py-1 backdrop-blur">
                  {series.year}
                </span>
              ) : null}
              {series.ageRating ? (
                <span className="rounded-full bg-white/15 px-2.5 py-1 backdrop-blur">
                  {series.ageRating}
                </span>
              ) : null}
              <span className="rounded-full bg-white/15 px-2.5 py-1 backdrop-blur">
                {t("series.episode_count", { count: series.episodeCount })}
              </span>
              {series.categories.slice(0, 3).map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-white/30 px-2.5 py-1 capitalize backdrop-blur"
                >
                  {categoryLabel(c, locale)}
                </span>
              ))}
            </div>

            {ratingBadge ? (
              <p className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-rose)]/95 px-3 py-1 text-xs font-bold text-white">
                <Heart className="h-3.5 w-3.5 fill-current" aria-hidden />
                {ratingBadge}
              </p>
            ) : null}

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <Link
                to={firstEpisode ? `/watch/${series.slug}/1/1` : "#"}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[var(--brand-paper)] px-6 text-sm font-semibold text-[var(--brand-ink)] hover:opacity-90"
              >
                <Play className="h-4 w-4 fill-current" aria-hidden />
                {t("series.cta.watch_first_free")}
              </Link>

              {series.trailerStreamVideoId ? (
                <button
                  type="button"
                  onClick={() => setTrailerOpen(true)}
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/60 px-6 text-sm font-semibold text-white hover:bg-white/15"
                >
                  ▶ {t("landing.hero.cta_trailer")}
                </button>
              ) : null}

              <button
                type="button"
                onClick={toggleList}
                disabled={listBusy}
                aria-pressed={inList}
                className={cn(
                  "inline-flex h-12 items-center justify-center gap-2 rounded-full border px-5 text-sm font-semibold transition-colors",
                  inList
                    ? "border-white bg-white/90 text-[var(--brand-ink)]"
                    : "border-white/60 text-white hover:bg-white/15",
                )}
              >
                {inList ? (
                  <Check className="h-4 w-4" aria-hidden />
                ) : (
                  <Plus className="h-4 w-4" aria-hidden />
                )}
                {inList ? t("series.cta.watchlist_in") : t("series.cta.watchlist_add")}
              </button>
            </div>

            <p className="pt-2 text-xs text-white/75">
              {t("series.free_promo_full", { free: freeCount, price: bundlePrice })}
            </p>
          </div>
        </div>
      </section>

      {/* Synopsis */}
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-2xl font-bold">{t("series.synopsis")}</h2>
        <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[var(--brand-ink)]/90 sm:text-base">
          {synopsis}
        </p>
      </section>

      {/* Episodes */}
      <section className="mx-auto max-w-3xl space-y-4 px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between">
          <h2 className="font-heading text-2xl font-bold">{t("series.episodes")}</h2>
          <p className="text-xs text-[var(--brand-muted)]">
            {t("series.first_n_free_short", { count: freeCount })}
          </p>
        </div>
        <ul className="space-y-2">
          {episodes.map((ep) => (
            <li key={ep._id}>
              <EpisodeRow
                ep={ep}
                seriesSlug={series.slug}
                locale={locale}
                onLockedClick={() => setUnlockEp(ep)}
              />
            </li>
          ))}
        </ul>
      </section>

      {/* Cast */}
      {series.cast.length > 0 ? (
        <section className="mx-auto max-w-5xl space-y-4 px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-bold">{t("series.cast")}</h2>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {series.cast.map((p) => (
              <li
                key={p.name + (p.role ?? "")}
                className="rounded-2xl bg-[var(--brand-pink-soft)] px-4 py-3"
              >
                <p className="text-sm font-semibold text-[var(--brand-ink)]">{p.name}</p>
                {p.role ? <p className="text-xs text-[var(--brand-muted)]">{p.role}</p> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Siblings */}
      {siblings.length > 0 ? (
        <section className="mx-auto max-w-7xl space-y-4 px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-bold">{t("series.more_from_category")}</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {siblings.map((s) => (
              <VerticalPosterCard
                key={s._id}
                href={`/series/${s.slug}`}
                titleTh={s.titleTh}
                titleEn={s.titleEn}
                taglineTh={s.taglineTh}
                taglineEn={s.taglineEn}
                posterUrl={s.posterUrl}
                freeEpisodeCount={s.freeEpisodeCount ?? 3}
                locale={locale}
              />
            ))}
          </div>
        </section>
      ) : null}

      {/* Trailer modal */}
      {series.trailerStreamVideoId ? (
        <Dialog open={trailerOpen} onOpenChange={setTrailerOpen}>
          <DialogContent className="max-w-md p-0 sm:max-w-lg">
            <DialogTitle className="sr-only">{t("series.trailer")}</DialogTitle>
            <div className="aspect-poster w-full overflow-hidden rounded-xl bg-black">
              {trailerOpen ? (
                <iframe
                  title="trailer"
                  src={`${STREAM_EMBED_ORIGIN}/${series.trailerStreamVideoId}?autoplay=true`}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                  allowFullScreen
                  className="h-full w-full border-0"
                />
              ) : null}
            </div>
          </DialogContent>
        </Dialog>
      ) : null}

      {/* Unlock sheet — wallet is the primary path; pack/bundle stay as
          fallbacks. The wallet flow is single-tap when balance is enough,
          otherwise routes to /account/wallet for top-up. */}
      <Sheet open={!!unlockEp} onOpenChange={(o) => !o && setUnlockEp(null)}>
        <SheetContent className="mx-auto max-w-md space-y-4">
          <SheetTitle className="font-heading text-xl">{t("series.unlock_sheet.title")}</SheetTitle>
          {unlockEp ? (
            <p className="text-sm text-[var(--brand-muted)]">
              EP{unlockEp.episodeNumber} · {locale === "th" ? unlockEp.titleTh : unlockEp.titleEn}
            </p>
          ) : null}

          {/* Primary: wallet path. Show balance state right inline so the
              user knows whether the click will charge or top-up first. */}
          {(() => {
            const unitPrice = unlockEp?.priceThb ?? 9;
            const balance = walletBalance ?? 0;
            const canCover = user && walletBalance != null && balance >= unitPrice;
            const short = Math.max(0, unitPrice - balance);
            const primaryLabel = !user
              ? t("series.unlock_sheet.signin_to_unlock", { price: unitPrice })
              : canCover
                ? t("series.unlock_sheet.use_wallet", { price: unitPrice, balance })
                : t("series.unlock_sheet.topup_and_unlock", { short, price: unitPrice });
            return (
              <Button
                variant="primary"
                className="w-full"
                disabled={unlockBusy || walletBalance === null}
                onClick={() => unlockWithWallet("episode")}
              >
                {unlockBusy ? "…" : primaryLabel}
              </Button>
            );
          })()}

          {unlockError ? (
            <p role="alert" className="text-xs text-[var(--brand-rose)]">
              {unlockError}
            </p>
          ) : null}

          {/* Wallet for the whole series — single ฿89 / ฿99 charge */}
          <Button
            variant="ghost"
            className="w-full"
            disabled={unlockBusy}
            onClick={() => unlockWithWallet("bundle")}
          >
            {t("series.unlock_sheet.bundle_wallet", { price: bundlePrice })}
          </Button>

          {/* Fallback path: pay-as-you-go via Stripe pack-of-2. Kept for
              users who prefer not to top up. */}
          <div className="border-t border-[var(--brand-border)] pt-3">
            <p className="mb-2 text-center text-xs text-[var(--brand-muted)]">
              {t("series.unlock_sheet.or_pay_directly")}
            </p>
            <Button
              variant="ghost"
              className="w-full text-sm"
              onClick={() => {
                if (!unlockEp) return;
                if (!user) {
                  navigate(`/login?next=/series/${series.slug}`);
                  return;
                }
                navigate(`/unlock/pack/${series._id}?startEpisodeId=${unlockEp._id}&count=2`);
              }}
            >
              {t("series.unlock_sheet.pack_of_two", { price: (unlockEp?.priceThb ?? 9) * 2 })}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function EpisodeRow({
  ep,
  seriesSlug,
  locale,
  onLockedClick,
}: {
  ep: Episode;
  seriesSlug: string;
  locale: "th" | "en";
  onLockedClick: () => void;
}) {
  const t = makeT(locale);
  const title = locale === "th" ? ep.titleTh : ep.titleEn;
  const runtimeMin = Math.max(1, Math.round(ep.durationSec / 60));
  if (ep.isFree) {
    return (
      <EpisodeListItem
        href={`/watch/${seriesSlug}/${ep.seasonNumber}/${ep.episodeNumber}`}
        episodeNumber={ep.episodeNumber}
        titleTh={ep.titleTh}
        titleEn={ep.titleEn}
        thumbnailUrl={ep.thumbnailUrl}
        runtimeMin={runtimeMin}
        state="free"
        locale={locale}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={onLockedClick}
      className="flex w-full items-center gap-3 rounded-xl bg-[var(--brand-paper)] p-3 text-left opacity-90 transition-opacity hover:opacity-100"
    >
      <div className="relative aspect-[16/10] w-24 shrink-0 overflow-hidden rounded-lg bg-[var(--brand-pink-soft)] sm:w-32">
        <img
          src={ep.thumbnailUrl}
          alt={title}
          className="absolute inset-0 h-full w-full object-cover opacity-70"
          loading="lazy"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <Lock className="h-5 w-5 text-white" aria-hidden />
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-mono text-xs text-[var(--brand-muted)]">EP{ep.episodeNumber}</p>
        <p className="truncate text-sm font-semibold text-[var(--brand-ink)]">{title}</p>
        <p className="text-xs text-[var(--brand-muted)]">
          {t("series.runtime_short", { minutes: runtimeMin })}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {typeof ep.priceThb === "number" ? (
          <PriceBadge amountThb={ep.priceThb} locale={locale} />
        ) : null}
        <Badge variant="locked">{t("series.ep_locked")}</Badge>
      </div>
    </button>
  );
}

// §5.11 rating rule — show "X% Liked" once meaningful (>= 10 votes), and
// surface "Hidden gem" when super-likes dominate on a low-volume series.
function deriveRatingBadge(r: SeriesRatings | null, locale: "th" | "en"): string | null {
  if (!r) return null;
  const t = makeT(locale);
  if (r.total >= 10) {
    const liked = r.like + r.super;
    const pct = Math.round((liked / Math.max(1, r.total)) * 100);
    return t("series.rating.percent_liked_short", { percent: pct });
  }
  if (r.super >= 2 * r.like && r.total >= 5) {
    return t("series.rating.hidden_gem_short");
  }
  return null;
}
